import { z } from "zod";
import { DemoPublicSchedulingConfigurationSchema, SchedulingAppointmentSourceSchema, } from "./demo-scheduling.js";
import { PatientMessagingDecisionChannelsSchema, PatientMessagingDecisionValueSchema, StaffMessagingDecisionChannelSchema, } from "./patient-messaging-consent.js";
import { IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
/**
 * Patient consent (PA-11 / EXECDEV-218): the clinic's versioned terms a patient agrees to when
 * they book online or open a questionnaire link, and what is recorded about each decision.
 *
 * Each clinic site has two documents, each a series of immutable versions: `privacy_notice`, the
 * one combined "terms of use and privacy notice" every patient agrees to, and
 * `electronic_messaging`, the optional "email and text messages" line, asked only while the
 * clinic actually sends messages. A patient may agree only to the current version of each; a
 * decision on an older one is refused with PUBLIC_TERMS_CHANGED_ERROR_CODE so the patient reads
 * the new text. Treatment consent is not collected here: it stays with the clinician in person.
 *
 * The stored text rules (ConsentDocumentTitleSchema, ConsentDocumentBodySchema) have a copy in
 * SQL, which counts UTF-16 code units and refuses the same characters. Change both together: a
 * document the database accepts but these schemas reject takes the clinic's booking page down.
 */
export const PATIENT_CONSENT_LIMITS = {
    /** Title length in UTF-16 code units. */
    titleMaxLength: 160,
    /** Body length in UTF-16 code units. */
    bodyMaxLength: 20_000,
    /** Earlier versions of a document an admin read returns, newest first. */
    documentHistory: 50,
    /** Decisions a chart read returns, newest first. */
    chartHistory: 500,
    /** The user agent recorded as evidence, in UTF-16 code units. */
    userAgentMaxLength: 512,
};
export const PATIENT_CONSENT_KINDS = ["privacy_notice", "electronic_messaging"];
export const PatientConsentKindSchema = z.enum(PATIENT_CONSENT_KINDS);
/** A terms agreement can only be accepted; the messaging line can be declined. */
export const PatientConsentDecisionSchema = z.enum(["accepted", "declined"]);
/** Where the patient decided: the booking review or a questionnaire (intake) link. */
export const PatientConsentChannelSchema = z.enum(["online_booking", "intake_link"]);
/**
 * `default`: the seeded text, which the clinic has not replaced (counsel review is a launch gate);
 * `clinic`: text the clinic published.
 */
export const ConsentDocumentSourceSchema = z.enum(["default", "clinic"]);
/**
 * The public lane's error code (HTTP 409) when a booking or questionnaire agreed to a version
 * that is no longer current: the clinic published new text after the page loaded. Nothing was
 * saved. The patient app reloads the documents, asks the patient to read and agree again, and
 * keeps the chosen time; it is not a booking conflict.
 */
export const PUBLIC_TERMS_CHANGED_ERROR_CODE = "TERMS_CHANGED";
// ---------------------------------------------------------------------------
// Document text
// ---------------------------------------------------------------------------
/** Unicode control characters (Cc: U+0000-U+001F and U+007F-U+009F). */
const CONTROL_CHARACTER = /\p{Cc}/u;
/** A UTF-16 surrogate standing alone, which PostgreSQL text cannot hold. */
const LONE_SURROGATE = /\p{Cs}/u;
/** No leading or trailing whitespace, as String.prototype.trim defines it. */
function isTrimmed(value) {
    return value === value.trim();
}
/**
 * Stored text, as the database holds it and every read returns it: no leading or trailing
 * whitespace, max UTF-16 code units, and no control character but the line feeds `allowLineFeeds`
 * permits. Reads are checked, never transformed, so a stored value is shown exactly.
 */
function storedText(label, max, allowLineFeeds) {
    return z
        .string()
        .min(1, `Enter the ${label}`)
        .max(max, `The ${label} must be ${max} characters or fewer`)
        .refine(isTrimmed, `The ${label} cannot start or end with a space or line break`)
        .refine((value) => !LONE_SURROGATE.test(value), `The ${label} contains a character that cannot be saved`)
        .refine((value) => !CONTROL_CHARACTER.test(allowLineFeeds ? value.replaceAll("\n", "") : value), allowLineFeeds
        ? `The ${label} cannot contain tabs or control characters`
        : `Keep the ${label} on one line, without tabs or control characters`);
}
/** One line of 1-160 UTF-16 code units. */
export const ConsentDocumentTitleSchema = storedText("title", PATIENT_CONSENT_LIMITS.titleMaxLength, false);
/**
 * Plain text of 1-20,000 UTF-16 code units with line feeds only. consentDocumentBlocks reads
 * its blank-line paragraphs, `## ` headings and `- ` lists; nothing in it is HTML.
 */
export const ConsentDocumentBodySchema = storedText("text", PATIENT_CONSENT_LIMITS.bodyMaxLength, true);
export const ConsentDocumentTextSchema = z
    .object({ title: ConsentDocumentTitleSchema, body: ConsentDocumentBodySchema })
    .strict();
/**
 * Line breaks as line feeds (a pasted CRLF or CR becomes LF) and the ends trimmed: what a
 * publish stores. The publish RPC applies the same normalization before its checks.
 */
export function normalizeConsentDocumentText(value) {
    return value.replace(/\r\n?/g, "\n").trim();
}
export const ConsentDocumentVersionNumberSchema = z.number().int().positive().max(2_147_483_647);
// ---------------------------------------------------------------------------
// What patients see and send
// ---------------------------------------------------------------------------
/** One version of a document as a patient reads it in the terms dialog. */
export const PublicConsentDocumentSchema = z
    .object({
    kind: PatientConsentKindSchema,
    version: ConsentDocumentVersionNumberSchema,
    title: ConsentDocumentTitleSchema,
    body: ConsentDocumentBodySchema,
})
    .strict();
const PublicPrivacyNoticeSchema = PublicConsentDocumentSchema.extend({
    kind: z.literal("privacy_notice"),
});
const PublicElectronicMessagingSchema = PublicConsentDocumentSchema.extend({
    kind: z.literal("electronic_messaging"),
});
/**
 * The documents a patient is asked about, current versions only. A null document is not asked
 * about. electronicMessaging is null unless the clinic offers messaging (it sends real text or
 * email messages), so a non-null value is what "messaging offered" means to the patient app.
 */
export const PublicConsentRequirementSchema = z
    .object({
    privacyNotice: PublicPrivacyNoticeSchema.nullable(),
    electronicMessaging: PublicElectronicMessagingSchema.nullable(),
})
    .strict();
/** The booking page always asks for the terms; the messaging line only when offered. */
export const PublicBookingConsentDocumentsSchema = PublicConsentRequirementSchema.extend({
    privacyNotice: PublicPrivacyNoticeSchema,
});
/** The terms agreement: a ticked box naming the version the patient was shown. */
export const ConsentAgreementSchema = z
    .object({
    version: ConsentDocumentVersionNumberSchema,
    accepted: z.literal(true, { error: "Agree to the terms and privacy notice" }),
})
    .strict();
/** The messaging line, shown ticked: unticking it declines. */
export const ConsentDecisionInputSchema = z
    .object({ version: ConsentDocumentVersionNumberSchema, accepted: z.boolean() })
    .strict();
/**
 * What a booking sends: the terms agreement, and the messaging decision only when the
 * configuration offered the line. It is part of the booking's idempotent replay.
 */
export const PublicBookingConsentSchema = z
    .object({
    privacyNotice: ConsentAgreementSchema,
    electronicMessaging: ConsentDecisionInputSchema.optional(),
})
    .strict();
/** What a questionnaire sends: an answer to each document the session asked about, if any. */
export const PublicIntakeConsentSchema = z
    .object({
    privacyNotice: ConsentAgreementSchema.optional(),
    electronicMessaging: ConsentDecisionInputSchema.optional(),
})
    .strict()
    .refine((value) => value.privacyNotice !== undefined || value.electronicMessaging !== undefined, "Send consent only with a decision in it");
// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------
/** HMAC-SHA256 of the visitor address, lowercase hex. The address itself is never stored. */
export const ConsentIpHashSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const ConsentUserAgentSchema = z
    .string()
    .min(1)
    .max(PATIENT_CONSENT_LIMITS.userAgentMaxLength)
    .refine(isTrimmed)
    .refine((value) => !CONTROL_CHARACTER.test(value) && !LONE_SURROGATE.test(value));
/**
 * The User-Agent header as consent evidence: control characters and broken surrogates removed,
 * trimmed and cut to 512 UTF-16 code units; undefined when nothing is left.
 */
export function consentEvidenceUserAgent(header) {
    const text = (header ?? "")
        .replace(/[\p{Cc}\p{Cs}]/gu, "")
        .trim()
        .slice(0, PATIENT_CONSENT_LIMITS.userAgentMaxLength)
        // The cut may have split a surrogate pair.
        .replace(/\p{Cs}$/u, "")
        .trimEnd();
    return text === "" ? undefined : text;
}
/** What agent-api records beside a decision (the RPC's p_consent_evidence). Never logged. */
export const ConsentEvidenceSchema = z
    .object({ ipHash: ConsentIpHashSchema, userAgent: ConsentUserAgentSchema.optional() })
    .strict();
// ---------------------------------------------------------------------------
// Staff: documents, publishing and the chart
// ---------------------------------------------------------------------------
/** A published version as staff read it. */
export const ConsentDocumentVersionSchema = PublicConsentDocumentSchema.extend({
    /** sha256 of {kind, title, body}: what each decision on this version records. */
    contentHash: Sha256Schema,
    source: ConsentDocumentSourceSchema,
    publishedAt: IsoDateTimeSchema,
});
function consentDocumentState(kind) {
    const version = ConsentDocumentVersionSchema.extend({ kind: z.literal(kind) });
    return z
        .object({
        current: version,
        /** Earlier versions, newest first; the oldest are left out past the limit. */
        history: z.array(version).max(PATIENT_CONSENT_LIMITS.documentHistory),
        /** The seeded text with this clinic's name, for "Restore default text". */
        defaultText: ConsentDocumentTextSchema,
    })
        .strict()
        .superRefine((value, context) => {
        let newer = value.current.version;
        for (const [index, earlier] of value.history.entries()) {
            if (earlier.version >= newer) {
                context.addIssue({
                    code: "custom",
                    message: "Earlier versions are listed newest first, below the current version",
                    path: ["history", index, "version"],
                });
            }
            newer = earlier.version;
        }
    });
}
/** The clinic's documents for the admin page (any staff role reads; clinic_admin publishes). */
export const PatientConsentDocumentsResponseSchema = z
    .object({
    /**
     * Whether patients are asked about electronic messaging: only once the clinic sends real
     * text or email messages. Until then the messaging document is kept but never shown.
     */
    messagingOffered: z.boolean(),
    privacyNotice: consentDocumentState("privacy_notice"),
    electronicMessaging: consentDocumentState("electronic_messaging"),
})
    .strict();
/**
 * Publishes new text for one document (the kind is in the route) against the current version.
 * Line breaks are normalized and the ends trimmed first. Text identical to the current version
 * publishes nothing; otherwise version expectedVersion + 1 is created, and patients who already
 * agreed keep the version they agreed to. A stale expectedVersion is refused with the
 * `consent_document_changed` error detail.
 */
export const PublishConsentDocumentRequestSchema = z
    .object({
    expectedVersion: ConsentDocumentVersionNumberSchema,
    title: z.string().transform(normalizeConsentDocumentText).pipe(ConsentDocumentTitleSchema),
    body: z.string().transform(normalizeConsentDocumentText).pipe(ConsentDocumentBodySchema),
})
    .strict();
export const PublishConsentDocumentResponseSchema = z
    .object({
    /** False when the text matched the current version, so nothing was published. */
    published: z.boolean(),
    documents: PatientConsentDocumentsResponseSchema,
})
    .strict();
/** One decision as the chart shows and exports it. */
export const PatientConsentRecordSchema = z
    .object({
    id: z.string().uuid(),
    kind: PatientConsentKindSchema,
    decision: PatientConsentDecisionSchema,
    documentVersion: ConsentDocumentVersionNumberSchema,
    documentHash: Sha256Schema,
    channel: PatientConsentChannelSchema,
    /** The line was shown already ticked (the messaging line). */
    presentedDefault: z.boolean(),
    recordedAt: IsoDateTimeSchema,
    /**
     * The appointment the decision was made for, which links it to this chart. A walk-in's
     * questionnaire link (PA-5) records its decisions against the walk-in's row, source walk_in.
     */
    appointment: z
        .object({
        id: z.string().uuid(),
        startAt: IsoDateTimeSchema,
        source: SchedulingAppointmentSourceSchema,
    })
        .strict(),
    /** For the export only; never shown on screen. */
    evidence: z
        .object({
        ipHash: ConsentIpHashSchema.nullable(),
        userAgent: ConsentUserAgentSchema.nullable(),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.kind === "privacy_notice" &&
        (value.decision !== "accepted" || value.presentedDefault)) {
        context.addIssue({
            code: "custom",
            message: "The terms can only be accepted, and never by a pre-ticked box",
            path: ["decision"],
        });
    }
});
/**
 * A messaging decision staff recorded (PA-8, owner decision O5): "Patient agreed" or "Patient
 * said no", heard at the front desk or on the phone, for texts, emails or both. Immutable; the
 * latest decision in scope wins (patient-messaging-consent.ts). Shown on the chart and in its
 * export with the patient's own decisions.
 */
export const StaffMessagingDecisionRecordSchema = z
    .object({
    id: z.string().uuid(),
    decision: PatientMessagingDecisionValueSchema,
    /** How staff heard it: front_desk or phone. */
    channel: StaffMessagingDecisionChannelSchema,
    messageChannels: PatientMessagingDecisionChannelsSchema,
    /** The electronic_messaging notice version read to the patient; always on an agreement. */
    documentVersion: ConsentDocumentVersionNumberSchema.nullable(),
    documentHash: Sha256Schema.nullable(),
    recordedAt: IsoDateTimeSchema,
    /** The staff member's display name, or "A staff member"; never an email address. */
    recordedByName: z.string().trim().min(1).max(200),
    /** The booking it was recorded on, or null when it was recorded on the chart. */
    appointment: z
        .object({
        id: z.string().uuid(),
        startAt: IsoDateTimeSchema,
        source: SchedulingAppointmentSourceSchema,
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.documentVersion === null) !== (value.documentHash === null)) {
        context.addIssue({
            code: "custom",
            message: "A notice version comes with its content hash",
            path: ["documentHash"],
        });
    }
    if (value.decision === "accepted" && value.documentVersion === null) {
        context.addIssue({
            code: "custom",
            message: "An agreement names the notice version read to the patient",
            path: ["documentVersion"],
        });
    }
});
/**
 * A patient's consent history at this site: decisions on appointments linked to the chart and,
 * from PA-8, the messaging decisions staff recorded on the chart or its bookings, newest first,
 * with the exact text of every version they name. Times are shown in the clinic's time zone. The
 * read is audited (the `?export=1` read as an export).
 */
export const PatientConsentHistorySchema = z
    .object({
    timeZone: DemoPublicSchedulingConfigurationSchema.shape.timeZone,
    consents: z.array(PatientConsentRecordSchema).max(PATIENT_CONSENT_LIMITS.chartHistory),
    messagingDecisions: z
        .array(StaffMessagingDecisionRecordSchema)
        .max(PATIENT_CONSENT_LIMITS.chartHistory),
    documents: z.array(ConsentDocumentVersionSchema).max(PATIENT_CONSENT_LIMITS.chartHistory),
})
    .strict()
    .superRefine((value, context) => {
    const hashes = new Map();
    for (const [index, document] of value.documents.entries()) {
        const key = `${document.kind}:${document.version}`;
        if (hashes.has(key)) {
            context.addIssue({
                code: "custom",
                message: "Each document version is listed once",
                path: ["documents", index],
            });
        }
        hashes.set(key, document.contentHash);
    }
    for (const [index, consent] of value.consents.entries()) {
        if (hashes.get(`${consent.kind}:${consent.documentVersion}`) !== consent.documentHash) {
            context.addIssue({
                code: "custom",
                message: "Every decision's document version is included, with the hash it recorded",
                path: ["consents", index, "documentVersion"],
            });
        }
    }
    for (const [index, decision] of value.messagingDecisions.entries()) {
        if (decision.documentVersion !== null &&
            hashes.get(`electronic_messaging:${decision.documentVersion}`) !== decision.documentHash) {
            context.addIssue({
                code: "custom",
                message: "Every decision's document version is included, with the hash it recorded",
                path: ["messagingDecisions", index, "documentVersion"],
            });
        }
    }
    const ids = value.messagingDecisions.map((decision) => decision.id);
    if (new Set(ids).size !== ids.length) {
        context.addIssue({
            code: "custom",
            message: "Each staff decision is listed once",
            path: ["messagingDecisions"],
        });
    }
});
/**
 * A document body as text blocks: a line starting `## ` is a heading, consecutive lines starting
 * `- ` are one list, and other lines run into a paragraph until a blank line. Returns text only,
 * never HTML, so markup in the body is shown as typed.
 */
export function consentDocumentBlocks(body) {
    const blocks = [];
    let paragraph = [];
    let list = [];
    const flush = () => {
        if (paragraph.length > 0)
            blocks.push({ kind: "paragraph", text: paragraph.join("\n") });
        if (list.length > 0)
            blocks.push({ kind: "list", items: list });
        paragraph = [];
        list = [];
    };
    for (const line of normalizeConsentDocumentText(body)
        .split("\n")
        .map((text) => text.trim())) {
        if (line === "") {
            flush();
        }
        else if (line.startsWith("## ")) {
            flush();
            blocks.push({ kind: "heading", text: line.slice(3).trim() });
        }
        else if (line.startsWith("- ")) {
            if (paragraph.length > 0)
                flush();
            list.push(line.slice(2).trim());
        }
        else {
            if (list.length > 0)
                flush();
            paragraph.push(line);
        }
    }
    flush();
    return blocks;
}
//# sourceMappingURL=patient-consent.js.map